import React, { useState, useEffect, useCallback } from 'react';
import { logError } from '@/lib/logger';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  QUIZ_QUESTIONS,
  RESULT_EXPLANATIONS,
  calculateAttachmentStyle,
  QuizHeader,
  QuestionCard,
  ResultScreen,
  SkipOption,
  LoadingScreen
} from '@/components/quiz';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AttachmentQuizPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [calculatedStyle, setCalculatedStyle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  // Check if quiz already completed
  useEffect(() => {
    const checkQuizStatus = async () => {
      try {
        const response = await axios.get(`${API}/attachment/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.completed || response.data.skipped) {
          navigate('/checkout');
          return;
        }
      } catch (err) {
        // If error, continue with quiz
        logError('Quiz status check failed:', err?.response?.status);
      } finally {
        setCheckingStatus(false);
      }
    };
    
    if (token) {
      checkQuizStatus();
    } else {
      setCheckingStatus(false);
    }
  }, [token, navigate]);

  const handleSelectAnswer = useCallback((style) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = style;
    setAnswers(newAnswers);
    
    // Auto-advance to next question after brief delay
    setTimeout(() => {
      if (currentQuestion < QUIZ_QUESTIONS.length - 1) {
        setCurrentQuestion(prev => prev + 1);
      } else {
        // Calculate result
        const result = calculateAttachmentStyle(newAnswers);
        setCalculatedStyle(result);
        setShowResult(true);
      }
    }, 300);
  }, [currentQuestion, answers]);

  const handleBack = useCallback(() => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  }, [currentQuestion]);

  const handleContinue = useCallback(async () => {
    if (!calculatedStyle) return;
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/attachment/save`,
        { attachment_style: calculatedStyle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate('/checkout');
    } catch (error) {
      toast.error('Failed to save your result');
      logError('Save error:', error);
    } finally {
      setSaving(false);
    }
  }, [calculatedStyle, token, navigate]);

  const handleSkip = useCallback(async () => {
    setSkipping(true);
    try {
      await axios.post(
        `${API}/attachment/save`,
        { attachment_style: null, skipped: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate('/checkout');
    } catch (error) {
      toast.error('Failed to skip quiz');
      logError('Skip error:', error);
    } finally {
      setSkipping(false);
    }
  }, [token, navigate]);

  if (checkingStatus) {
    return <LoadingScreen />;
  }

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#FBF9F6', fontFamily: 'Jost, sans-serif' }}
    >
      <div className="flex-1 px-6 py-12 max-w-xl mx-auto w-full">
        {!showResult ? (
          <>
            <QuizHeader
              currentQuestion={currentQuestion + 1}
              totalQuestions={QUIZ_QUESTIONS.length}
              onBack={handleBack}
              canGoBack={currentQuestion > 0}
            />
            
            <QuestionCard
              question={QUIZ_QUESTIONS[currentQuestion]}
              selectedAnswer={answers[currentQuestion]}
              onSelectAnswer={handleSelectAnswer}
            />
            
            <SkipOption onSkip={handleSkip} skipping={skipping} />
          </>
        ) : (
          <ResultScreen
            result={RESULT_EXPLANATIONS[calculatedStyle]}
            onContinue={handleContinue}
            saving={saving}
          />
        )}
      </div>
    </div>
  );
};

export default AttachmentQuizPage;
